import { Placeholder } from '@tiptap/extensions';
import { type Editor, EditorContent, type EditorOptions, type Extensions, useEditor } from '@tiptap/react';
import isNil from 'lodash/isNil';
import { forwardRef, useEffect, useImperativeHandle, useMemo, useRef, useState } from 'react';
import { useIntl } from 'react-intl';

import { Button, ButtonVariant } from '@ui/components/base/button';
import { Div } from '@ui/components/base/div';
import { Separator } from '@ui/components/base/separator';
import { CustomIconButton } from '@ui/components/buttons/customIconButton';
import { CollapsibleChevron } from '@ui/components/collapses/collapsibleChevron';
import { RichTextColorPopover } from '@ui/components/editor/RichTextColorPopover';
import {
  insertTemplateImage,
  insertTemplateTable,
  insertTemplateToken,
  redrawEditorSilently,
} from '@ui/components/editor/richTextEditorCommands';
import { RICH_TEXT_EXTENSIONS } from '@ui/components/editor/richTextExtensions';
import {
  applyBlockType,
  applyFontSize,
  buildFontSizeChoices,
  FORMAT_ITEMS,
  HISTORY_ITEMS,
  NEW_TABLE_SIZE,
  type RichTextEditorMode,
  TEMPLATE_ALIGN_ITEMS,
  TEMPLATE_BLOCK_ITEMS,
  TEMPLATE_CLEAR_ITEMS,
  TEMPLATE_MARK_ITEMS,
  type ToolbarItem,
  type ToolbarMenuOptions,
  useToolbarState,
} from '@ui/components/editor/richTextToolbar';
import { RichTextUrlPopover } from '@ui/components/editor/RichTextUrlPopover';
import { buildEditableAttributes, RICH_TEXT_PROSE_CLASS_NAME } from '@ui/components/editor/richTextUtils';
import { linkifyUrlBeforeCaret } from '@ui/components/editor/templateLinkUtils';
import { type TemplateTagProps, useTemplateTagConfiguration } from '@ui/components/editor/templateTagExtension';
import { createTemplateTagSuggestionBridge, TemplateTagSuggestion } from '@ui/components/editor/templateTagSuggestion';
import { TemplateTagSuggestionMenu } from '@ui/components/editor/TemplateTagSuggestionMenu';
import { TEMPLATE_TEXT_EXTENSIONS } from '@ui/components/editor/templateTextExtensions';
import {
  TEMPLATE_BLOCK_TYPES,
  TEMPLATE_HIGHLIGHT_COLORS,
  TEMPLATE_INVALID_SURFACE_CLASS_NAME,
  TEMPLATE_TABLE_SIZES,
  TEMPLATE_TEXT_COLORS,
  TEMPLATE_TEXT_PROSE_CLASS_NAME,
  toTemplateEditorHtml,
} from '@ui/components/editor/templateTextUtils';
import {
  BaselineIcon,
  HighlighterIcon,
  MinusIcon,
  PanelTopIcon,
  PlusIcon,
  TableCellsMergeIcon,
  TableCellsSplitIcon,
  TableIcon,
  TrashIcon,
} from '@ui/components/icons';
import { ActionsMenu, type ActionsMenuAction } from '@ui/components/menus/actionsMenu';
import { Tooltip } from '@ui/components/tooltips/Tooltip';
import { cn } from '@ui/lib/utils';

const TOOLBAR_CLASS_NAME = 'flex-wrap items-center gap-0.5 border-b border-input px-1.5 py-1';
const TOOLBAR_SEPARATOR_CLASS_NAME = 'mx-1 h-5';
const TOOLBAR_GROUP_CLASS_NAME = 'items-center gap-0.5';
// Same border and focus ring as the neighbouring text fields; the editable area sets outline-none.
const SURFACE_CLASS_NAME = cn(
  'overflow-hidden rounded-lg border border-input bg-background transition-colors',
  'focus-within:border-ring focus-within:ring-3 focus-within:ring-ring/50'
);

export interface RichTextEditorHandle {
  insertImage: (source: string) => void;
  insertToken: (token: string) => void;
}

export interface RichTextEditorProps extends TemplateTagProps {
  value: string;
  onChange: (value: string) => void;
  className?: string;
  mode?: RichTextEditorMode;
  placeholder?: string;
  invalid?: boolean;
  readOnly?: boolean;
  fullHeight?: boolean;
  onFocus?: () => void;
  onBlur?: () => void;
  'aria-label'?: string;
  'aria-labelledby'?: string;
}

const readEditorHtml = (editor: Editor): string => (editor.isEmpty ? '' : editor.getHTML());

const RichTextEditor = forwardRef<RichTextEditorHandle, RichTextEditorProps>(
  (
    {
      value,
      onChange,
      className,
      mode = 'rich',
      placeholder,
      invalid = false,
      readOnly = false,
      fullHeight = false,
      onFocus,
      onBlur,
      'aria-label': ariaLabel,
      'aria-labelledby': ariaLabelledBy,
      tags,
      isTokenAvailable,
      unavailableTagTitle,
    },
    ref
  ) => {
    const { formatMessage } = useIntl();
    const isTemplate = mode === 'template';
    const isReadOnly = isTemplate && readOnly;
    const editable = !isReadOnly;
    const placeholderRef = useRef<string | undefined>(placeholder);
    const [initialContent] = useState<string>(() => (isTemplate ? toTemplateEditorHtml(value) : value));
    const syncedValueRef = useRef<string>(value);
    const keepsSelectionRef = useRef<boolean>(false);
    const [suggestionBridge] = useState(createTemplateTagSuggestionBridge);
    const [extensions] = useState<Extensions>(() =>
      isTemplate
        ? [
            ...TEMPLATE_TEXT_EXTENSIONS,
            Placeholder.configure({ placeholder: () => placeholderRef.current ?? '' }),
            TemplateTagSuggestion.configure({ bridge: suggestionBridge }),
          ]
        : RICH_TEXT_EXTENSIONS
    );
    const editorProps = useMemo<EditorOptions['editorProps']>(
      () => ({
        attributes: buildEditableAttributes({
          className: isTemplate
            ? cn(TEMPLATE_TEXT_PROSE_CLASS_NAME, 'flex-1 px-3 py-2 outline-none')
            : cn(RICH_TEXT_PROSE_CLASS_NAME, 'min-h-48 px-3 py-2 outline-none'),
          multiline: true,
          ariaLabel,
          ariaLabelledBy,
          invalid,
          readOnly: isReadOnly,
        }),
      }),
      [isTemplate, ariaLabel, ariaLabelledBy, invalid, isReadOnly]
    );
    const editor = useEditor({
      extensions,
      content: isTemplate ? initialContent : value,
      // The admintool is a Next app, so the first pass runs on the server where ProseMirror has no DOM.
      immediatelyRender: false,
      editable,
      editorProps,
      onUpdate: ({ editor: updatedEditor }) => {
        const html = readEditorHtml(updatedEditor);
        syncedValueRef.current = html;
        onChange(html);
      },
      onFocus: () => {
        keepsSelectionRef.current = true;
        onFocus?.();
      },
      onBlur: ({ editor: blurredEditor }) => {
        if (isTemplate) {
          linkifyUrlBeforeCaret(blurredEditor);
        }
        onBlur?.();
      },
    });
    useTemplateTagConfiguration(editor, { tags, isTokenAvailable, unavailableTagTitle }, isTemplate);
    const toolbarState = useToolbarState(editor, mode);

    useImperativeHandle(ref, () => ({
      insertImage: (source: string) => {
        if (isNil(editor)) {
          return;
        }
        if (isTemplate) {
          insertTemplateImage(editor.chain().focus(), { src: source });
          return;
        }
        editor.chain().focus().setImage({ src: source }).run();
      },
      insertToken: (token: string) => {
        if (isNil(editor) || !isTemplate) {
          return;
        }
        insertTemplateToken(editor, token, keepsSelectionRef.current);
      },
    }));

    // Language tabs and Translate all change the value without going through onUpdate. While the author
    // types, `value` is only a (possibly late) echo of the editor's own content and must not overwrite it.
    useEffect(() => {
      if (isNil(editor) || editor.isFocused) {
        return;
      }
      if (isTemplate && syncedValueRef.current === value) {
        return;
      }
      if (readEditorHtml(editor) === value) {
        return;
      }
      editor.commands.setContent(isTemplate ? toTemplateEditorHtml(value) : value, { emitUpdate: false });
      syncedValueRef.current = value;
      keepsSelectionRef.current = false;
    }, [editor, isTemplate, value]);

    useEffect(() => {
      if (isNil(editor) || editor.isEditable === editable) {
        return;
      }
      editor.setEditable(editable, false);
    }, [editor, editable]);

    useEffect(() => {
      placeholderRef.current = placeholder;
      if (!isTemplate || isNil(editor) || editor.isDestroyed) {
        return;
      }
      redrawEditorSilently(editor);
    }, [editor, isTemplate, placeholder]);

    if (isNil(editor) || isNil(toolbarState)) {
      return null;
    }

    const { insideTable } = toolbarState;
    const rowAndColumnActions: ActionsMenuAction[] = [
      {
        label: formatMessage({ id: 'richTextEditor.addRow' }),
        icon: PlusIcon,
        disabled: !insideTable,
        onClick: () => editor.chain().focus().addRowAfter().run(),
      },
      {
        label: formatMessage({ id: 'richTextEditor.removeRow' }),
        icon: MinusIcon,
        disabled: !insideTable,
        onClick: () => editor.chain().focus().deleteRow().run(),
      },
      {
        label: formatMessage({ id: 'richTextEditor.addColumn' }),
        icon: PlusIcon,
        disabled: !insideTable,
        onClick: () => editor.chain().focus().addColumnAfter().run(),
      },
      {
        label: formatMessage({ id: 'richTextEditor.removeColumn' }),
        icon: MinusIcon,
        disabled: !insideTable,
        onClick: () => editor.chain().focus().deleteColumn().run(),
      },
    ];
    const deleteTableAction: ActionsMenuAction = {
      label: formatMessage({ id: 'richTextEditor.deleteTable' }),
      icon: TrashIcon,
      destructive: true,
      disabled: !insideTable,
      onClick: () => editor.chain().focus().deleteTable().run(),
    };
    const richTableActions: ActionsMenuAction[] = [
      {
        label: formatMessage({ id: 'richTextEditor.insertTable' }),
        icon: TableIcon,
        onClick: () => editor.chain().focus().insertTable(NEW_TABLE_SIZE).run(),
      },
      ...rowAndColumnActions,
      deleteTableAction,
    ];

    const renderToolbarItem = (item: ToolbarItem) => {
      const { active, disabled: itemDisabled } = toolbarState.items[item.messageId];
      const label = formatMessage({ id: item.messageId });
      return (
        <CustomIconButton
          key={item.messageId}
          type="button"
          icon={item.icon}
          variant={active ? ButtonVariant.SECONDARY : ButtonVariant.GHOST}
          aria-pressed={isNil(item.isActive) ? undefined : active}
          aria-label={label}
          tooltipText={label}
          disabled={itemDisabled}
          onClick={() => item.run(editor)}
        />
      );
    };

    if (!isTemplate) {
      return (
        <Div column className={cn(SURFACE_CLASS_NAME, className)}>
          <Div row className={TOOLBAR_CLASS_NAME}>
            {HISTORY_ITEMS.map(renderToolbarItem)}
            <Separator orientation="vertical" className={TOOLBAR_SEPARATOR_CLASS_NAME} />
            {FORMAT_ITEMS.map(renderToolbarItem)}
            <Separator orientation="vertical" className={TOOLBAR_SEPARATOR_CLASS_NAME} />
            <RichTextUrlPopover editor={editor} kind="link" active={toolbarState.linkActive} />
            <RichTextUrlPopover editor={editor} kind="image" />
            <ActionsMenu
              align="start"
              actions={richTableActions}
              trigger={
                <CustomIconButton
                  type="button"
                  icon={TableIcon}
                  variant={insideTable ? ButtonVariant.SECONDARY : ButtonVariant.GHOST}
                  aria-label={formatMessage({ id: 'richTextEditor.table' })}
                />
              }
            />
          </Div>
          <EditorContent editor={editor} />
        </Div>
      );
    }

    const { fontSize } = toolbarState;
    const defaultFontSizeLabel = formatMessage({ id: 'richTextEditor.defaultFontSize' });
    const blockTypeActions: ActionsMenuAction[] = TEMPLATE_BLOCK_TYPES.map((blockType) => ({
      label: formatMessage({ id: `richTextEditor.blockTypes.${blockType}` }),
      onClick: () => applyBlockType(editor, blockType),
    }));
    const fontSizeActions: ActionsMenuAction[] = buildFontSizeChoices(fontSize, defaultFontSizeLabel).map((choice) => ({
      label: choice.label,
      onClick: () => applyFontSize(editor, choice.value),
    }));

    const renderToolbarMenu = ({ label, currentLabel, actions, className: menuClassName }: ToolbarMenuOptions) => (
      <Tooltip content={label}>
        <ActionsMenu
          align="start"
          contentClassName="w-40"
          actions={actions}
          trigger={
            <Button
              type="button"
              variant={ButtonVariant.GHOST}
              size="sm"
              aria-label={label}
              className={cn('justify-between font-normal', menuClassName)}
            >
              {currentLabel}
              <CollapsibleChevron open={false} className="fill-current group-aria-expanded/button:rotate-0" />
            </Button>
          }
        />
      </Tooltip>
    );

    const templateTableActions: ActionsMenuAction[] = [
      ...TEMPLATE_TABLE_SIZES.map((size) => ({
        label: formatMessage({ id: 'richTextEditor.insertTableOfSize' }, { rows: size.rows, columns: size.cols }),
        icon: TableIcon,
        onClick: () => insertTemplateTable(editor, size),
      })),
      ...rowAndColumnActions,
      {
        label: formatMessage({ id: 'richTextEditor.toggleHeaderRow' }),
        icon: PanelTopIcon,
        disabled: !insideTable,
        onClick: () => editor.chain().focus().toggleHeaderRow().run(),
      },
      {
        label: formatMessage({ id: 'richTextEditor.mergeCells' }),
        icon: TableCellsMergeIcon,
        disabled: !toolbarState.canMergeCells,
        onClick: () => editor.chain().focus().mergeCells().run(),
      },
      {
        label: formatMessage({ id: 'richTextEditor.splitCell' }),
        icon: TableCellsSplitIcon,
        disabled: !toolbarState.canSplitCell,
        onClick: () => editor.chain().focus().splitCell().run(),
      },
      deleteTableAction,
    ];
    const tableLabel = formatMessage({ id: 'richTextEditor.table' });

    return (
      <Div
        column
        data-slot="rich-text-editor"
        data-invalid={invalid}
        data-read-only={isReadOnly}
        className={cn(
          isReadOnly ? 'min-w-0' : cn(SURFACE_CLASS_NAME, 'isolate overflow-clip', TEMPLATE_INVALID_SURFACE_CLASS_NAME),
          fullHeight && 'min-h-0 flex-1',
          className
        )}
      >
        {isReadOnly ? null : (
          <Div
            row
            role="group"
            aria-label={formatMessage({ id: 'richTextEditor.toolbar' })}
            className={cn(TOOLBAR_CLASS_NAME, 'sticky top-0 z-10 shrink-0 bg-inherit')}
          >
            <Div row className={TOOLBAR_GROUP_CLASS_NAME}>
              {HISTORY_ITEMS.map(renderToolbarItem)}
            </Div>
            <Separator orientation="vertical" className={TOOLBAR_SEPARATOR_CLASS_NAME} />
            <Div row className={TOOLBAR_GROUP_CLASS_NAME}>
              {renderToolbarMenu({
                label: formatMessage({ id: 'richTextEditor.blockType' }),
                currentLabel: formatMessage({ id: `richTextEditor.blockTypes.${toolbarState.blockType}` }),
                actions: blockTypeActions,
                className: 'w-32',
              })}
              {renderToolbarMenu({
                label: formatMessage({ id: 'richTextEditor.fontSize' }),
                currentLabel: fontSize ?? defaultFontSizeLabel,
                actions: fontSizeActions,
                className: 'w-28',
              })}
            </Div>
            <Separator orientation="vertical" className={TOOLBAR_SEPARATOR_CLASS_NAME} />
            <Div row className={TOOLBAR_GROUP_CLASS_NAME}>
              {TEMPLATE_MARK_ITEMS.map(renderToolbarItem)}
              <RichTextColorPopover
                editor={editor}
                label={formatMessage({ id: 'richTextEditor.textColor' })}
                resetLabel={formatMessage({ id: 'richTextEditor.defaultTextColor' })}
                icon={BaselineIcon}
                colors={TEMPLATE_TEXT_COLORS}
                value={toolbarState.color}
                onApply={(color) => editor.chain().setColor(color).run()}
                onReset={() => editor.chain().unsetColor().run()}
              />
              <RichTextColorPopover
                editor={editor}
                label={formatMessage({ id: 'richTextEditor.highlightColor' })}
                resetLabel={formatMessage({ id: 'richTextEditor.noHighlight' })}
                icon={HighlighterIcon}
                colors={TEMPLATE_HIGHLIGHT_COLORS}
                value={toolbarState.backgroundColor}
                onApply={(color) => editor.chain().setBackgroundColor(color).run()}
                onReset={() => editor.chain().unsetBackgroundColor().run()}
              />
              {TEMPLATE_CLEAR_ITEMS.map(renderToolbarItem)}
            </Div>
            <Separator orientation="vertical" className={TOOLBAR_SEPARATOR_CLASS_NAME} />
            <Div row className={TOOLBAR_GROUP_CLASS_NAME}>
              {TEMPLATE_BLOCK_ITEMS.map(renderToolbarItem)}
            </Div>
            <Separator orientation="vertical" className={TOOLBAR_SEPARATOR_CLASS_NAME} />
            <Div row className={TOOLBAR_GROUP_CLASS_NAME}>
              {TEMPLATE_ALIGN_ITEMS.map(renderToolbarItem)}
            </Div>
            <Separator orientation="vertical" className={TOOLBAR_SEPARATOR_CLASS_NAME} />
            <Div row className={TOOLBAR_GROUP_CLASS_NAME}>
              <RichTextUrlPopover editor={editor} kind="link" active={toolbarState.linkActive} />
              <RichTextUrlPopover editor={editor} kind="image" />
              <ActionsMenu
                align="start"
                actions={templateTableActions}
                trigger={
                  <CustomIconButton
                    type="button"
                    icon={TableIcon}
                    variant={insideTable ? ButtonVariant.SECONDARY : ButtonVariant.GHOST}
                    aria-label={tableLabel}
                    tooltipText={tableLabel}
                  />
                }
              />
            </Div>
          </Div>
        )}
        <Div
          className={
            isReadOnly
              ? undefined
              : cn(
                  'flex resize-y flex-col overflow-auto',
                  fullHeight ? 'min-h-20 flex-auto contain-size' : 'h-80 min-h-40'
                )
          }
        >
          <EditorContent editor={editor} className="flex grow flex-col" />
        </Div>
        {isReadOnly ? null : <TemplateTagSuggestionMenu bridge={suggestionBridge} />}
      </Div>
    );
  }
);
RichTextEditor.displayName = 'RichTextEditor';

export { RichTextEditor };
export type { RichTextEditorMode };
